/**
 * src/components/ui/Table.jsx
 * Tabla responsiva con header sticky y estado vacío/loading.
 *
 * Fila expandible (28-08, hilo de distribución): `claveExpandida` +
 * `renderExpandida(row)` pintan un panel a todo lo ancho JUSTO DEBAJO de la
 * fila cuya clave (`row.id` o `claveFila(row)`) coincida. Nació para la ficha
 * de uso de Uso y churn, que se abría al fondo de la página y había que
 * perseguirla; bajo su propia fila, el contexto no se pierde.
 */

export function Table({ columns, data: rawData, onRowClick, loading, emptyMessage = 'Sin datos',
                        sortKey, sortDir, onSort,
                        claveFila, claveExpandida, renderExpandida }) {
  const data = Array.isArray(rawData) ? rawData : []
  const _clave = (row, i) => (claveFila ? claveFila(row) : (row.id ?? i))
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontSize: 'var(--text-base)', fontFamily: 'var(--font-ui)',
      }}>
        <thead>
          <tr>
            {columns.map(col => {
              const sortable = col.sortable && onSort
              const activa = sortable && sortKey === col.key
              return (
              <th
                key={col.key}
                onClick={sortable ? () => onSort(col.key) : undefined}
                title={sortable ? 'Ordenar' : undefined}
                style={{
                  textAlign: col.align || 'left',
                  padding: 'var(--space-3) var(--space-4)',
                  fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-medium)',
                  color: activa ? 'var(--color-accent)' : 'var(--color-text-3)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  borderBottom: '1px solid var(--color-border)',
                  whiteSpace: 'nowrap',
                  width: col.width,
                  cursor: sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                }}
              >
                {col.label}{activa ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: 'var(--space-7)', color: 'var(--color-text-3)' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-mono)',
                }}>
                  <span style={{
                    width: '14px', height: '14px', border: '2px solid var(--color-border)',
                    borderTopColor: 'var(--color-accent)', borderRadius: 'var(--radius-pill)',
                    animation: 'spin 0.7s linear infinite', display: 'inline-block',
                  }} />
                  Cargando...
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{
                textAlign: 'center', padding: 'var(--space-7)',
                color: 'var(--color-text-3)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
              }}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, i) => {
              const clave = _clave(row, i)
              const expandida = renderExpandida && claveExpandida != null && clave === claveExpandida
              return [
                <tr
                  key={clave}
                  onClick={() => onRowClick?.(row)}
                  style={{
                    borderBottom: '1px solid var(--color-border)',
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background var(--t-fast) var(--ease)',
                    background: expandida ? 'var(--color-surface-2)' : 'transparent',
                  }}
                  onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = 'var(--color-surface-2)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = expandida ? 'var(--color-surface-2)' : 'transparent' }}
                >
                  {columns.map(col => (
                    <td
                      key={col.key}
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        color: col.mono ? 'var(--color-text)' : 'var(--color-text)',
                        fontFamily: col.mono ? 'var(--font-mono)' : 'var(--font-ui)',
                        fontSize: col.mono ? 'var(--text-sm)' : 'var(--text-base)',
                        textAlign: col.align || 'left',
                        whiteSpace: col.nowrap ? 'nowrap' : 'normal',
                      }}
                    >
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>,
                expandida && (
                  <tr key={`${clave}-detalle`}>
                    <td colSpan={columns.length} style={{
                      padding: 'var(--space-3) var(--space-4) var(--space-4)',
                      borderBottom: '1px solid var(--color-border)',
                      background: 'var(--color-surface-2)',
                    }}>
                      {renderExpandida(row)}
                    </td>
                  </tr>
                ),
              ]
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
